import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import framework from 'utils/framework';
const { wrapFunctionalComponent } = framework;
import LanguageLocaleUtils from 'utils/LanguageLocale';
import userActions from 'actions/UserActions';
import passkeysUtils from 'utils/passkeys';
import authenticationUtils from 'utils/Authentication';
import Storage from 'utils/localStorage/Storage';
import UrlUtils from 'utils/Url';
import { Text, Button } from 'components/ui';
import {
    fontSizes, lineHeights, fontWeights, space
} from 'style/config';
import ResetPasswordEN from 'pages/Auth/ResetPassword/locales/ResetPassword_en_US';
import ResetPasswordFR from 'pages/Auth/ResetPassword/locales/ResetPassword_fr_CA';

const getText = LanguageLocaleUtils.getLocalisedDataFromResource(ResetPasswordEN, ResetPasswordFR);

const PROFILE_URL = '/profile/BeautyInsider';
const PASSKEY_RESET_FLAG = 'passkeyResetRedirect';
const REDIRECT_TTL_MS = Storage.MINUTES * 10;

/**
 * RESET_SUCCESS — SIM-18 / ACS-4355 / SUB-TASK-4.
 *
 * Inline replacement for the `showInfoModal({ title: 'Reset Successful', ... })`
 * dispatch that `components/ResetPassword/ResetPassword.ctrlr.jsx` fired
 * post-reset. Preserves the ctrlr's `goToProfile` semantics verbatim: when
 * passkeys are enabled, set the `passkeyResetRedirect` session flag
 * (10-minute TTL) before redirecting to `/profile/BeautyInsider`. When
 * auth-service is enabled AND `profileStatus !== 0`, run
 * `userActions.getUserFull(null, goToProfile)` first so the profile fetch
 * refreshes the user slice before navigation.
 */
const ResetSuccess = () => {
    const dispatch = useDispatch();
    // Derive `shouldMakeUserFullCall` on click from the live auth-service
    // flag + Redux `user.profileStatus` — matches the ctrlr contract and
    // decouples from whether SetNewPassword remembered to pass the derived
    // flag through `sharedState`. Both signals are cheap and always current.
    const profileStatus = useSelector(state => state?.user?.profileStatus);

    const goToProfile = () => {
        if (passkeysUtils.isPasskeyEnabled()) {
            Storage.session.setItem(PASSKEY_RESET_FLAG, { ts: Date.now() }, REDIRECT_TTL_MS);
        }

        UrlUtils.redirectTo(PROFILE_URL);
    };

    const handleViewProfile = () => {
        // Single canonical source (matches SIM-1 ctrlr wiring after ACS-3079).
        const shouldCallUserFull = Boolean(authenticationUtils.isAuthServiceEnabled() && profileStatus !== 0);

        if (shouldCallUserFull) {
            dispatch(userActions.getUserFull(null, goToProfile));

            return;
        }

        goToProfile();
    };

    return (
        <>
            <Text
                is='h1'
                fontSize={fontSizes.lg}
                fontWeight={fontWeights.bold}
                lineHeight={lineHeights.tight}
                marginBottom={4}
            >
                {getText('resetSuccessful')}
            </Text>

            <Text
                is='p'
                fontSize={fontSizes.md}
                lineHeight={lineHeights.base}
                marginBottom={5}
            >
                {getText('passwordHasBeenReset')}
            </Text>

            <Button
                variant='primary'
                type='button'
                width='100%'
                paddingY={space[3]}
                onClick={handleViewProfile}
            >
                {getText('viewProfileButton')}
            </Button>
        </>
    );
};

export default wrapFunctionalComponent(ResetSuccess, 'ResetSuccess');
